import { useEffect, useRef } from "react";
import { X } from "lucide-react";

/** Centred desktop dialog that becomes a full-viewport workflow on mobile. */
export default function ResponsiveDialog({ open = true, title, subtitle, onClose, headerActions, children, footer, busy = false, closeOnEscape = true, labelledBy, className = "" }) {
  const panel = useRef(null);
  // Event-handler props are commonly passed as inline functions. Keeping their
  // latest values in refs prevents the dialog lifecycle effect from tearing
  // down and re-running whenever a controlled form re-renders while typing.
  // Re-running that effect restored focus to the first control, which looked
  // like every input lost focus after a single character.
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  const closeOnEscapeRef = useRef(closeOnEscape);
  onCloseRef.current = onClose;
  busyRef.current = busy;
  closeOnEscapeRef.current = closeOnEscape;
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusable = () => [...(panel.current?.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])') || [])];
    focusable()[0]?.focus();
    const keydown = (event) => {
      if (event.key === "Escape" && closeOnEscapeRef.current && !busyRef.current) onCloseRef.current?.();
      if (event.key !== "Tab") return;
      const items = focusable(); if (!items.length) return;
      const first = items[0], last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", keydown);
    return () => { document.removeEventListener("keydown", keydown); document.body.style.overflow = previousOverflow; previous?.focus?.(); };
  }, [open]);
  if (!open) return null;
  const headingId = labelledBy || `dialog-${String(title || "content").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center sm:p-4" data-testid="responsive-dialog-overlay">
    <button type="button" tabIndex={-1} className="absolute inset-0 h-full w-full cursor-default bg-black/50 p-0" aria-label="Close dialog" onClick={() => !busy && onClose?.()} />
    <section ref={panel} role="dialog" aria-modal="true" aria-labelledby={headingId} aria-busy={busy} className={`relative flex h-[100dvh] w-full flex-col bg-white shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-3xl sm:overflow-hidden sm:rounded-xl ${className}`} data-testid="responsive-dialog">
      <header className="flex shrink-0 items-start justify-between gap-4 border-b px-5 py-4"><div><h2 id={headingId} className="text-xl font-black">{title}</h2>{subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}</div><div className="flex items-center gap-2">{headerActions}<button type="button" onClick={onClose} disabled={busy} aria-label={`Close ${title || "dialog"}`} className="p-2 hover:bg-gray-100 disabled:opacity-50"><X size={18}/></button></div></header>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="responsive-dialog-scroll">{children}</div>
      {footer && <footer className="shrink-0 border-t bg-white px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4" data-testid="responsive-dialog-footer">{footer}</footer>}
    </section>
  </div>;
}
